import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    inject,
    Input,
    OnChanges,
    SimpleChanges
} from '@angular/core';
import {MaterialModule} from '../../material.module';

@Component({
    selector: 'app-screenshot-carousel',
    standalone: true,
    imports: [MaterialModule],
    templateUrl: './screenshot-carousel.component.html',
    styleUrls: ['./screenshot-carousel.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ScreenshotCarouselComponent implements OnChanges {
    @Input() images: readonly string[] = [];
    @Input() altPrefix = 'Product';
    @Input() ariaLabel = 'Product screenshots';
    currentIndex = 0;
    private readonly cdr = inject(ChangeDetectorRef);

    get canGoPrev(): boolean {
        return this.currentIndex > 0;
    }

    get canGoNext(): boolean {
        return this.currentIndex < this.images.length - 1;
    }

    get trackTransform(): string {
        return `translateX(-${this.currentIndex * 100}%)`;
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['images']) {
            this.currentIndex = 0;
        }

        this.currentIndex = this.clampIndex(this.currentIndex);
    }

    prev(): void {
        this.goTo(this.currentIndex - 1);
    }

    next(): void {
        this.goTo(this.currentIndex + 1);
    }

    goTo(index: number): void {
        const clamped = this.clampIndex(index);
        if (clamped === this.currentIndex) {
            return;
        }
        this.currentIndex = clamped;
        this.cdr.markForCheck();
    }

    altText(index: number): string {
        const prefix = (this.altPrefix ?? '').trim() || 'Screenshot';
        return `${prefix} screenshot ${index + 1}`;
    }

    onKeydown(event: KeyboardEvent): void {
        if (event.key === 'ArrowLeft') {
            this.prev();
            event.preventDefault();
            return;
        }

        if (event.key === 'ArrowRight') {
            this.next();
            event.preventDefault();
        }
    }

    private clampIndex(index: number): number {
        if (!this.images || this.images.length === 0) {
            return 0;
        }

        return Math.min(Math.max(index, 0), this.images.length - 1);
    }
}

